import React, { useEffect, useState } from "react";
import { 
  Form, 
  Input, 
  Button, 
  Select, 
  message, 
  DatePicker, 
  InputNumber,
  Upload,
  Card,
  Row,
  Col,
  Space
} from "antd";
import { UploadOutlined } from "@ant-design/icons";
import { useAuth } from "../Context";
import { useNotification } from "../NotificationContext";
import { firestore, storage } from "../../firebase";
import { collection, addDoc, doc, updateDoc } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import moment from "moment";

const { Option } = Select;
const { TextArea } = Input;

const AgregarEquipoForm = ({ equipo, sucursal, onClose }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const { currentUser } = useAuth();
  const openNotificationWithIcon = useNotification();

  useEffect(() => {
    if (equipo) {
      // Si estamos editando, llenar el formulario
      form.setFieldsValue({
        ...equipo,
        fechaCompra: equipo.fechaCompra ? moment(equipo.fechaCompra.toDate()) : null,
        fechaGarantia: equipo.fechaGarantia ? moment(equipo.fechaGarantia.toDate()) : null,
      });
    }
  }, [equipo, form]);

  const handleFinish = async (values) => {
    if (!sucursal) {
      message.error("Error: No se pudo obtener la sucursal");
      return;
    }

    setLoading(true);
    try {
      const equipoData = {
        ...values,
        fechaCompra: values.fechaCompra?.toDate() || null,
        fechaGarantia: values.fechaGarantia?.toDate() || null,
        sucursal,
        creadoEn: equipo ? equipo.creadoEn : new Date(),
        actualizadoEn: new Date(),
        creadoPor: equipo ? equipo.creadoPor : currentUser?.email || "",
        actualizadoPor: currentUser?.email || ""
      };

      if (equipo) {
        // Actualizar equipo existente
        await updateDoc(doc(firestore, `sucursal/${sucursal}/equipo/${equipo.id}`), equipoData);
        message.success("Equipo actualizado correctamente");
        openNotificationWithIcon("success", "Éxito", "El equipo se actualizó correctamente");
      } else {
        // Crear nuevo equipo
        await addDoc(collection(firestore, `sucursal/${sucursal}/equipo`), equipoData);
        message.success("Equipo registrado correctamente");
        openNotificationWithIcon("success", "Éxito", "El equipo se registró correctamente");
      }
      
      form.resetFields();
      if (onClose) onClose();
    } catch (error) {
      console.error("Error al guardar equipo:", error);
      message.error("Error al guardar el equipo");
      openNotificationWithIcon("error", "Error", "Hubo un problema al guardar el equipo");
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (file) => {
    if (!file) return;

    setUploading(true);
    try {
      const storageRef = ref(storage, `equipos/${sucursal}/${Date.now()}_${file.name}`);
      const snapshot = await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(snapshot.ref);
      
      form.setFieldsValue({ imagenUrl: downloadURL });
      message.success("Imagen subida correctamente");
    } catch (error) {
      console.error("Error al subir imagen:", error);
      message.error("Error al subir la imagen");
    } finally {
      setUploading(false);
    }
  };

  return (
    <Card>
      <Form
        form={form}
        layout="vertical"
        onFinish={handleFinish}
        initialValues={{
          estado: "operativo",
          fechaCompra: moment()
        }}
      >
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item 
              name="nombre" 
              label="Nombre del Equipo" 
              rules={[{ required: true, message: "Ingrese el nombre del equipo" }]}
            >
              <Input placeholder="Ej: Perforadora XYZ-123" />
            </Form.Item>
          </Col>
          
          <Col span={12}>
            <Form.Item 
              name="tipo" 
              label="Tipo de Equipo" 
              rules={[{ required: true, message: "Seleccione el tipo de equipo" }]}
            >
              <Select placeholder="Seleccionar tipo">
                <Option value="perforadora">Perforadora</Option>
                <Option value="excavadora">Excavadora</Option>
                <Option value="compactadora">Compactadora</Option>
                <Option value="generador">Generador</Option>
                <Option value="bomba">Bomba</Option>
                <Option value="herramienta_electrica">Herramienta Eléctrica</Option>
                <Option value="herramienta_manual">Herramienta Manual</Option>
                <Option value="instrumento_medicion">Instrumento de Medición</Option>
                <Option value="vehiculo">Vehículo</Option>
                <Option value="otro">Otro</Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={8}>
            <Form.Item name="marca" label="Marca">
              <Input placeholder="Ej: Caterpillar" />
            </Form.Item>
          </Col>
          
          <Col span={8}>
            <Form.Item name="modelo" label="Modelo">
              <Input placeholder="Ej: 320D" />
            </Form.Item>
          </Col>
          
          <Col span={8}>
            <Form.Item name="numeroSerie" label="Número de Serie">
              <Input placeholder="Ej: ABC123456" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={8}>
            <Form.Item 
              name="estado" 
              label="Estado" 
              rules={[{ required: true, message: "Seleccione el estado" }]}
            >
              <Select>
                <Option value="operativo">Operativo</Option>
                <Option value="mantenimiento">En Mantenimiento</Option>
                <Option value="reparacion">En Reparación</Option>
                <Option value="fuera_servicio">Fuera de Servicio</Option>
              </Select>
            </Form.Item>
          </Col>
          
          <Col span={8}>
            <Form.Item name="ubicacion" label="Ubicación">
              <Input placeholder="Ej: Almacén A, Sitio 1" />
            </Form.Item>
          </Col>
          
          <Col span={8}>
            <Form.Item name="responsable" label="Responsable">
              <Input placeholder="Persona a cargo" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={8}>
            <Form.Item name="fechaCompra" label="Fecha de Compra">
              <DatePicker 
                style={{ width: "100%" }}
                format="DD/MM/YYYY"
                placeholder="Seleccionar fecha"
              />
            </Form.Item>
          </Col>
          
          <Col span={8}>
            <Form.Item name="fechaGarantia" label="Vencimiento de Garantía">
              <DatePicker 
                style={{ width: "100%" }}
                format="DD/MM/YYYY"
                placeholder="Seleccionar fecha"
              />
            </Form.Item>
          </Col>
          
          <Col span={8}>
            <Form.Item name="precioCompra" label="Precio de Compra">
              <InputNumber
                style={{ width: "100%" }}
                placeholder="0.00"
                min={0}
                step={0.01}
                formatter={value => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                parser={value => value.replace(/\$\s?|(,*)/g, '')}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item name="proveedor" label="Proveedor">
              <Input placeholder="Nombre del proveedor" />
            </Form.Item>
          </Col>
          
          <Col span={12}>
            <Form.Item name="numeroFactura" label="Número de Factura">
              <Input placeholder="Número de factura de compra" />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item name="especificaciones" label="Especificaciones Técnicas">
          <TextArea 
            rows={3}
            placeholder="Detalles técnicos, capacidades, dimensiones, etc."
          />
        </Form.Item>

        <Form.Item name="observaciones" label="Observaciones">
          <TextArea 
            rows={2}
            placeholder="Notas adicionales, historial de mantenimiento, etc."
          />
        </Form.Item>

        <Form.Item name="imagenUrl" label="Imagen del Equipo">
          <Upload
            accept="image/*"
            beforeUpload={(file) => {
              handleUpload(file);
              return false;
            }}
            maxCount={1}
            listType="picture"
          >
            <Button 
              icon={<UploadOutlined />} 
              loading={uploading}
              disabled={uploading}
            >
              {uploading ? "Subiendo..." : "Subir Imagen"}
            </Button>
          </Upload>
        </Form.Item>

        <Form.Item>
          <Space>
            <Button 
              type="primary" 
              htmlType="submit" 
              loading={loading}
              disabled={loading || uploading}
            >
              {loading ? "Guardando..." : equipo ? "Actualizar Equipo" : "Registrar Equipo"}
            </Button>
            
            {onClose && (
              <Button onClick={onClose}>
                Cancelar
              </Button>
            )}
          </Space>
        </Form.Item>
      </Form>
    </Card>
  );
};

export default AgregarEquipoForm;
